import { fromBaseClone, fromBaseRecord } from './from-base-context';
import type { FromBaseRow } from './from-base-context';
import { surfaceField } from './from-base-encounter-media';
import { mutableSurface } from './mutable-surface';

const viewerKeys = new Set(['permissions', 'viewer']);
const inventoryObservationSkipKeys = new Set(['catalog', '_entity_base', '_inventory_base']);
function cloneEntity<T>(value: T, inventories?: object[], shared = false): T {
    return fromBaseClone(value, undefined, shared, shared ? viewerKeys : undefined, inventories ? copy => {
        if (fromBaseRecord(copy) && fromBaseRecord(copy.inventory)) inventories.push(copy);
    } : undefined, inventoryObservationSkipKeys);
}

/** Viewer grants are served separately from the shared browser copy. */
export function sharedEntityComputed(output: unknown, inventories?: object[]): unknown {
    return cloneEntity(output, inventories, true);
}

/** Shared preparation owns plain data; each cold host receives its own tree. */
export function mutableEntityComputed(output: unknown): unknown { return mutableSurface(output, viewerKeys); }
export function prepareEntityComputed(output: unknown): Promise<unknown> { return Promise.resolve(mutableEntityComputed(output)); }

/** SELECT order is newest-first. Keep the saved latest opt-out for each ability. */
export function attachEntitySavedSections(base: FromBaseRow, output: unknown, ownedOutput = false, inventories?: object[]): unknown {
    if (!fromBaseRecord(output)) return output;
    const row = ownedOutput ? output : cloneEntity(output, inventories), seen = new Set<string>();
    for (const key of ['encounter_exploration_states', 'encounter_exploration_states_arr'])
        if (Array.isArray(base[key])) row[key] = cloneEntity(base[key], inventories);
    const triggers = [];
    for (const saved of Array.isArray(base.triggers) ? base.triggers : []) {
        if (!fromBaseRecord(saved)) continue;
        const ability = String(surfaceField(saved, 'trigger_ability_id') ?? '');
        if (!ability || seen.has(ability)) continue;
        seen.add(ability);
        triggers.push(cloneEntity(saved, inventories));
    }
    if (triggers.length) row.triggers = triggers;
    else delete row.triggers;
    if (Array.isArray(row.encounter_creatures_arr) && Array.isArray(base.encounter_creatures_arr)) {
        const inputs = new Map(base.encounter_creatures_arr.filter(fromBaseRecord).map(member => [String(member.entity_id).toUpperCase(), member]));
        row.encounter_creatures_arr = row.encounter_creatures_arr.map(member => {
            const id = fromBaseRecord(member) && fromBaseRecord(member.encounter_creatures)
                ? surfaceField(member.encounter_creatures, 'encounter_creature_id') : null;
            const input = inputs.get(String(id).toUpperCase());
            return input ? attachEntitySavedSections(input, member, ownedOutput, inventories) : member;
        });
    }
    return row;
}
